import { Routes } from '@angular/router';
import { AuthenticatedUserLayoutComponent } from 'src/shared/layout/default-layout/authenticated-user-layout.component';
import { authGuard } from 'src/modules/security/guards/auth.guard';
import { AppRouteEnum } from 'src/app/enums/app-route.enum';
import { createAccessGuard } from 'src/modules/security/guards/user-access.guard';
import { UserLevel } from 'src/modules/security/enums/user-level.enum';
import { pscGuard } from 'src/modules/security/guards/psc.guard';
import { supplyOrderGuard } from 'src/modules/security/guards/supply-order.guard';
import { analyticsGuard } from 'src/modules/security/guards/analytics.guard';
import { ForbiddenPageComponent } from 'src/app/components/forbidden-page.component/forbidden-page.component';
import { LoginLayoutComponent } from 'src/shared/layout/login-layout/login-layout.component';
import { LoginComponent } from 'src/modules/security/pages/login.component';
import { loginAuthGuard } from 'src/modules/security/guards/login-auth.guard';
import { LogoutComponent } from 'src/modules/security/components/logout-button/logout.component';
import { LogoutSuccessComponent } from 'src/modules/security/components/logout-button/logout-success.component';
import { RestrictedLayoutComponent } from 'src/shared/layout/restricted-layout/restricted-layout.component';
import { HeaderComponent } from 'src/app/components/header/header/header.component';
import { FooterComponent } from 'src/shared/components/footer/footer.component';
import { Component } from '@angular/core';
import { chainCanActivateFns } from 'src/app/utils/chain-guards.util';
import { pscHouseAccountGuard } from 'src/modules/security/guards/psc-house-account.guard';
import { pscSearchClearCurrentLocationGuard } from 'src/modules/security/guards/psc-search-clear-current-location.guard';
import { manifestDependenciesGuard } from 'src/app/pages/manifests/guards/manifest-dependencies.guard';
import { preaccessionManifestsDependenciesGuard } from 'src/app/pages/preaccession-manifests/guards/preaccession-manifests-dependencies.guard';
import { currentAccountGuard } from 'src/app/guards/current-account.guard';
import { homeAccountRequiredGuard } from 'src/app/guards/home-account-required.guard';
import { appDefaultNavigationGuard } from 'src/app/guards/app-default-navigation.guard';

@Component({ template: '' })
class DummyComponent {}

const headerRoute = {
  path: '',
  outlet: 'header',
  component: HeaderComponent
};

const footerRoute = {
  path: '',
  outlet: 'footer',
  component: FooterComponent
};

const accessGuardResultsOnly = createAccessGuard(UserLevel.ResultsOnly, [AppRouteEnum.Forbidden], { shouldNotInclude: true });
const accessGuardOrdersOnly = createAccessGuard(UserLevel.OrdersOnly, [AppRouteEnum.Forbidden], { shouldNotInclude: true });

export const routes: Routes = [
  {
    path: '',
    component: AuthenticatedUserLayoutComponent,
    canActivate: [authGuard, homeAccountRequiredGuard],
    children: [
      headerRoute,
      footerRoute,
      {
        path: AppRouteEnum.Patients,
        data: { title: 'Patients' },
        loadChildren: () => import('./pages/patients/patients.module').then(m => m.PatientsModule),
        canActivate: [authGuard, currentAccountGuard]
      },
      {
        path: AppRouteEnum.Orders,
        data: { title: 'Orders' },
        canActivate: [authGuard, accessGuardResultsOnly, currentAccountGuard],
        loadChildren: () => import('./pages/orders/orders.module').then(m => m.OrdersModule)
      },
      {
        path: AppRouteEnum.PscSearch,
        data: { title: 'PSC Search' },
        canActivate: [authGuard, pscGuard, pscSearchClearCurrentLocationGuard],
        loadChildren: () => import('./pages/psc-search/psc-search.routes').then(m => m.pscSearchRoutes)
      },
      {
        path: AppRouteEnum.PscHouseAccount,
        data: { title: 'PSC House Account' },
        canActivate: [authGuard, pscGuard, pscHouseAccountGuard],
        loadChildren: () => import('./pages/psc-house-account/psc-house-account.routes').then(m => m.pscHouseAccountRoutes)
      },
      {
        path: AppRouteEnum.PriceEstimator,
        data: { title: 'Price Estimator' },
        loadChildren: () => import('./pages/price-estimator/price-estimator.module').then(m => m.PriceEstimatorModule),
        canActivate: [authGuard]
      },
      {
        path: AppRouteEnum.Chat,
        loadChildren: () => import('./pages/chat/chat.routes').then(m => m.chatRoutes),
        canActivate: [authGuard, analyticsGuard]
      },
      {
        path: AppRouteEnum.TestCompendium,
        data: { title: 'Test Compendium' },
        loadChildren: () => import('./pages/test-compendium/test-compendium.routes').then(m => m.testCompendiumRoutes),
        canActivate: [authGuard]
      },
      {
        path: AppRouteEnum.Manifests,
        data: { title: 'Manifest List' },
        canActivate: [chainCanActivateFns(authGuard, accessGuardResultsOnly, manifestDependenciesGuard)],
        loadChildren: () => import('./pages/manifests/manifests.module').then(m => m.ManifestsModule)
      },
      {
        path: AppRouteEnum.PreaccessionManifests,
        data: { title: 'Preaccession Manifest List' },
        canActivate: [chainCanActivateFns(authGuard, accessGuardResultsOnly, preaccessionManifestsDependenciesGuard)],
        loadChildren: () => import('./pages/preaccession-manifests/preaccession-manifests-routes').then(m => m.preaccessionManifestsRoutes)
      },
      {
        path: AppRouteEnum.Results,
        data: { title: 'Results Inbox' },
        canActivate: [authGuard, accessGuardOrdersOnly, currentAccountGuard],
        loadChildren: () => import('./pages/order-results/order-results.module').then(m => m.OrderResultsModule)
      },
      {
        path: AppRouteEnum.BatchResults,
        data: { title: 'Batch Results' },
        canActivate: [authGuard, accessGuardOrdersOnly, currentAccountGuard],
        loadChildren: () => import('./pages/batch-results/batch-results.routes').then(m => m.batchResultsRoutes)
      },
      {
        path: AppRouteEnum.GlobalResultsSearch,
        data: { title: 'Global Results Search' },
        canActivate: [authGuard, accessGuardOrdersOnly, currentAccountGuard],
        loadChildren: () => import('./pages/global-results-search/global-results-search-routes').then(m => m.globalResultsSearchRoutes)
      },
      {
        path: AppRouteEnum.GlobalResultsReports,
        data: { title: 'Global Results Reports' },
        canActivate: [authGuard, accessGuardOrdersOnly, currentAccountGuard],
        loadChildren: () => import('./pages/global-results-reports/global-results-reports-routes').then(m => m.globalResultsReportsRoutes)
      },
      {
        path: AppRouteEnum.SupplyOrders,
        data: { title: 'Supply Orders' },
        canActivate: [authGuard, supplyOrderGuard, currentAccountGuard],
        loadChildren: () => import('./pages/supply-orders/supply-orders.module').then(m => m.SupplyOrdersModule)
      },
      {
        path: AppRouteEnum.PracticeAdmin,
        data: { title: 'Practice Admin' },
        canActivate: [authGuard, createAccessGuard(UserLevel.PracticeAdmin, [AppRouteEnum.Forbidden]), currentAccountGuard],
        loadChildren: () => import('./pages/practice-admin/practice-admin.routes').then(m => m.practiceAdminRoutes)
      },
      {
        path: AppRouteEnum.Forbidden,
        pathMatch: 'full',
        data: { title: 'Forbidden' },
        component: ForbiddenPageComponent
      },
      {
        path: '',
        // Redirect user to /login or /patients. Property 'redirectTo' allows to load children modules even guard disallow that
        canActivate: [chainCanActivateFns(authGuard, appDefaultNavigationGuard)],
        component: DummyComponent,
        pathMatch: 'full'
      }
    ]
  },
  {
    path: '',
    component: LoginLayoutComponent,
    children: [
      footerRoute,
      {
        path: AppRouteEnum.Login,
        pathMatch: 'full',
        data: { title: 'Login' },
        component: LoginComponent,
        outlet: undefined,
        canActivate: [loginAuthGuard]
      },
      {
        path: AppRouteEnum.Logout,
        pathMatch: 'full',
        data: { title: 'Logout' },
        component: LogoutComponent,
        canActivate: [authGuard]
      },
      {
        path: AppRouteEnum.LogoutSuccess,
        pathMatch: 'full',
        data: { title: 'Logged Out' },
        component: LogoutSuccessComponent
      },
      {
        path: '',
        pathMatch: 'full',
        redirectTo: `/${AppRouteEnum.Login}`
      }
    ]
  },
  {
    path: '',
    component: RestrictedLayoutComponent,
    children: [
      {
        path: AppRouteEnum.Support,
        data: { title: 'Support' },
        loadChildren: () => import('./pages/support/support.module').then(m => m.SupportModule)
      }
    ]
  },
  { path: AppRouteEnum.Wildcard, redirectTo: `/${AppRouteEnum.Patients}`, pathMatch: 'full' }
];
